import { css } from '@emotion/react';
import { Button, Icon, Text } from '@inflearn/ds-react-next';
import { VerifyPhoneModal } from '@inflearn/modals';
import { faArrowRight } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { AI_INTERVIEWS_URL } from '../../../constants/urls';
import useCheckLoginSession from '../../../hooks/useCheckLoginSession';
import { useMockInterviewVoucherFreeCreateMutation } from '../../../mutations/useMockInterviewVoucherFreeCreateMutation';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import useClientAccountCredentialsQuery from '../../../queries/useClientAccountCredentialsQuery';
import useMockInterviewVoucherQuery, {
  getMockInterviewVoucherQueryKey,
} from '../../../queries/useMockInterviewVoucherQuery';
import { MockInterviewConsentModal } from './MockInterviewConsentModal';
import { MockInterviewEnvironmentGuideModal } from './MockInterviewEnvironmentGuideModal';
import { LANDING_EASE, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './landing/constants';
import {
  type EnvironmentGuideVariant,
  getEnvironmentGuideVariant,
} from './utils/getEnvironmentGuideVariant';

/**
 * 모의면접 시작 버튼.
 * 로딩/로그인/전화인증/이용권 상태를 분기하고,
 * 각 분기의 비즈니스 로직은 하위 분기 컴포넌트에 위임한다.
 */
export function MockInterviewStartButton() {
  const { isLoggedIn, isLoading: isCheckLoginSessionLoading } = useCheckLoginSession();
  const { data, isLoading: isGetMockInterviewVoucherLoading } = useMockInterviewVoucherQuery({});
  const credentialsQuery = useClientAccountCredentialsQuery({ enabled: isLoggedIn });
  const isPhoneVerified = Boolean(credentialsQuery.data?.phoneNumber);
  const isGetClientAccountCredentialsLoading = isLoggedIn && credentialsQuery.isInitialLoading;

  // 단일 이용권 정책(유저당 이용권 1개)이라 대표 이용권 하나로 판정한다.
  const voucher = data?.items?.[0];
  const status = voucher?.mockInterview?.status;
  const mockInterviewId = voucher?.mockInterview?.mockInterviewId;

  if (
    isCheckLoginSessionLoading ||
    isGetMockInterviewVoucherLoading ||
    isGetClientAccountCredentialsLoading
  ) {
    return (
      <MockInterviewButton loading disabled>
        모의면접 시작하기
      </MockInterviewButton>
    );
  }

  if (!isLoggedIn) {
    return <AuthButton />;
  }

  if (!isPhoneVerified) {
    return <PhoneButton />;
  }

  if (status === 'ON_AIR' && mockInterviewId != null) {
    return <ContinueMockInterviewButton mockInterviewId={mockInterviewId} />;
  }

  if (status === 'REPORT_GENERATED' && mockInterviewId != null) {
    return <ReportMockInterviewButton mockInterviewId={mockInterviewId} />;
  }

  return <StartMockInterviewButton needsIssue={voucher == null} />;
}

type MockInterviewButtonProps = {
  children: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

/**
 * 모든 분기 버튼의 시각 표현을 담당하는 프레젠테이셔널 컴포넌트(다크 글래스 CTA).
 * 클릭 시 모바일이거나 PC라도 화면이 좁으면 분기별 onClick 대신 환경 안내 모달을 띄운다.
 */
function MockInterviewButton({ children, onClick, ...props }: MockInterviewButtonProps) {
  const [guideVariant, setGuideVariant] = useState<EnvironmentGuideVariant | null>(null);

  const handleClick = () => {
    const variant = getEnvironmentGuideVariant();

    if (variant) {
      setGuideVariant(variant);
      return;
    }

    onClick?.();
  };

  return (
    <>
      <Button
        type="button"
        css={styles.cta}
        rightSection={<Icon icon={faArrowRight} size={18} color="#fff" />}
        onClick={handleClick}
        {...props}
      >
        <Text component="span" css={styles.ctaLabel}>
          {children}
        </Text>
      </Button>
      {guideVariant && (
        <MockInterviewEnvironmentGuideModal
          opened
          variant={guideVariant}
          onClose={() => setGuideVariant(null)}
        />
      )}
    </>
  );
}

/** 로그인 분기: 미로그인 상태에서 클릭 시 로그인 모달을 연다. */
function AuthButton() {
  const { open } = useLoginModalContext();

  return <MockInterviewButton onClick={open}>모의면접 시작하기</MockInterviewButton>;
}

/**
 * 전화인증 분기: 미인증 상태에서 클릭 시 인증 모달을 연다.
 * 인증 성공 시 크레덴셜 쿼리를 무효화하면 리렌더되어 상위 컨테이너가 다음 분기로 넘긴다.
 */
function PhoneButton() {
  const queryClient = useQueryClient();
  const [isPhoneModalOpen, phoneModalHandlers] = useDisclosure(false);

  const handleVerifySuccess = () => {
    queryClient.invalidateQueries(useClientAccountCredentialsQuery.queryKey);
    phoneModalHandlers.close();
  };

  return (
    <>
      <MockInterviewButton onClick={phoneModalHandlers.open}>모의면접 시작하기</MockInterviewButton>
      <VerifyPhoneModal
        isOpen={isPhoneModalOpen}
        onClose={phoneModalHandlers.close}
        spot="mock-interview"
        onVerifySuccess={handleVerifySuccess}
      />
    </>
  );
}

type StartMockInterviewButtonProps = {
  /** 이용권이 없어 무료 발급이 선행되어야 하는지 여부 */
  needsIssue: boolean;
};

/**
 * 새 면접 시작 분기: 이용권 없음/미사용 이용권/재응시 등 새 면접을 시작하는 경우 렌더된다.
 * 클릭 시 항상 동의 모달을 열고, 동의 후 (필요 시 발급 →) 시작 페이지로 이동한다.
 */
function StartMockInterviewButton({ needsIssue }: StartMockInterviewButtonProps) {
  const queryClient = useQueryClient();
  const { mutate, isLoading: isIssuing } = useMockInterviewVoucherFreeCreateMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMockInterviewVoucherQueryKey() });
      },
    },
  });
  const [isConsentModalOpen, consentModalHandlers] = useDisclosure(false);

  const handleConsentAgree = () => {
    consentModalHandlers.close();

    // 이용권이 없으면 발급 후 이동, 있으면 바로 이동
    if (needsIssue) {
      mutate(undefined, {
        onSuccess: () => {
          window.location.href = AI_INTERVIEWS_URL.NEW();
        },
      });
      return;
    }

    window.location.href = AI_INTERVIEWS_URL.NEW();
  };

  return (
    <>
      <MockInterviewButton loading={isIssuing} onClick={consentModalHandlers.open}>
        모의면접 시작하기
      </MockInterviewButton>
      <MockInterviewConsentModal
        opened={isConsentModalOpen}
        onClose={consentModalHandlers.close}
        onAgree={handleConsentAgree}
      />
    </>
  );
}

type MockInterviewSessionButtonProps = {
  mockInterviewId: number;
};

/** 이어하기 분기: 진행 중(ON_AIR) 면접이 있으면 렌더되며, 클릭 시 세션 페이지로 이동한다. */
function ContinueMockInterviewButton({ mockInterviewId }: MockInterviewSessionButtonProps) {
  const handleClick = () => {
    window.location.href = AI_INTERVIEWS_URL.SESSION(mockInterviewId);
  };

  return <MockInterviewButton onClick={handleClick}>모의면접 이어하기</MockInterviewButton>;
}

/** 보고서 분기: 보고서 생성 완료(REPORT_GENERATED) 시 렌더되며, 클릭 시 보고서 페이지로 이동한다. */
function ReportMockInterviewButton({ mockInterviewId }: MockInterviewSessionButtonProps) {
  const handleClick = () => {
    window.location.href = AI_INTERVIEWS_URL.REPORT(mockInterviewId);
  };

  return <MockInterviewButton onClick={handleClick}>보고서 보러가기</MockInterviewButton>;
}

const styles = {
  cta: css({
    flex: 'none',
    height: 48,
    minWidth: 200,
    padding: `0 ${LANDING_SPACE.ctaPadX}`,
    borderRadius: 10,
    border: 'none',
    background: '#171719',
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    transition: `background ${LANDING_EASE}, transform ${LANDING_EASE}`,
    '&:hover': {
      background: '#2e2f33',
    },
    '&:active': {
      transform: 'scale(.98)',
    },
    [LANDING_MOBILE_MQ]: {
      flex: '1 0 0',
      width: '100%',
      minWidth: 0,
    },
  }),
  ctaLabel: css({
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    letterSpacing: '-0.01em',
  }),
};
